import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import {
  HIDDEN_KEYS,
  filterHidden,
  readHiddenIds,
  restoreItem,
} from "../utils/hiddenHistory";
import { formatAppDate, formatAppDateTime } from "../utils/dateFormat";

const TABS = [
  { id: "sales", label: "Sales" },
  { id: "stock", label: "Stock" },
  { id: "loans", label: "Loans" },
  { id: "salary", label: "Salary" },
  { id: "expenses", label: "Expenses" },
];

const formatNumber = (value) => Number(value ?? 0).toFixed(2);

export default function History() {
  const [activeTab, setActiveTab] = useState("sales");
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loans, setLoans] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [hiddenCounts, setHiddenCounts] = useState({});

  const refreshCounts = useCallback(() => {
    setHiddenCounts({
      sales: readHiddenIds(HIDDEN_KEYS.sales).length,
      stock: readHiddenIds(HIDDEN_KEYS.stock).length,
      loans: readHiddenIds(HIDDEN_KEYS.loans).length,
      salary: readHiddenIds(HIDDEN_KEYS.salary).length,
      expenses: readHiddenIds(HIDDEN_KEYS.expenses).length,
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      axios.get(`${API_BASE_URL}/api/sales`),
      axios.get(`${API_BASE_URL}/api/stocks`),
      axios.get(`${API_BASE_URL}/api/loans`),
      axios.get(`${API_BASE_URL}/api/salaries`),
      axios.get(`${API_BASE_URL}/api/expenses`),
    ]);

    if (results[0].status === "fulfilled") {
      setSales(Array.isArray(results[0].value.data) ? results[0].value.data : []);
    }
    if (results[1].status === "fulfilled") {
      setStocks(Array.isArray(results[1].value.data) ? results[1].value.data : []);
    }
    if (results[2].status === "fulfilled") {
      setLoans(Array.isArray(results[2].value.data) ? results[2].value.data : []);
    }
    if (results[3].status === "fulfilled") {
      setSalaries(
        Array.isArray(results[3].value.data) ? results[3].value.data : [],
      );
    }
    if (results[4].status === "fulfilled") {
      setExpenses(
        Array.isArray(results[4].value.data) ? results[4].value.data : [],
      );
    }
    refreshCounts();
    setLoading(false);
  }, [refreshCounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRestore = (key, id) => {
    restoreItem(key, id);
    refreshCounts();
    fetchData();
  };

  if (loading) {
    return <Loader message="Loading history…" fullPage />;
  }

  const hiddenSales = filterHidden(sales, HIDDEN_KEYS.sales);
  const hiddenStocks = filterHidden(stocks, HIDDEN_KEYS.stock);
  const hiddenLoans = filterHidden(loans, HIDDEN_KEYS.loans);
  const hiddenSalaries = filterHidden(salaries, HIDDEN_KEYS.salary);
  const hiddenExpenses = filterHidden(expenses, HIDDEN_KEYS.expenses);

  const totalHidden = Object.values(hiddenCounts).reduce((a, b) => a + b, 0);

  const renderEmpty = (label) => (
    <p className="text-muted text-center py-4 mb-0">
      No hidden {label} records. Items you remove on other pages appear here.
    </p>
  );

  return (
    <div className="container-fluid app-container history-page">
      <p className="text-muted small mb-3">
        Removed items are stored on this device only. Use Restore to show them
        again on the main pages.
      </p>
      <div className="tabs history-tabs mb-3">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {hiddenCounts[tab.id] > 0 ? ` (${hiddenCounts[tab.id]})` : ""}
          </button>
        ))}
      </div>
      <div className="card app-screen-card">
        {totalHidden === 0 ? (
          <p className="text-muted text-center py-5 mb-0">
            History is empty. Hide a record from Sales, Stock, Loan, Salary, or
            Expenses to see it here.
          </p>
        ) : (
          <>
            {activeTab === "sales" && (
              <div className="table-responsive app-table-wrap">
                {hiddenSales.length === 0 ? (
                  renderEmpty("sales")
                ) : (
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Driver</th>
                        <th>Item</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hiddenSales.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDateTime(item.sale_date)}
                          </td>
                          <td data-label="Customer">
                            {item.customer?.name || "-"}
                          </td>
                          <td data-label="Driver">{item.driver_name || "-"}</td>
                          <td data-label="Item">{item.stock?.name || "-"}</td>
                          <td data-label="Total">
                            {formatNumber(item.total_amount)}
                          </td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleRestore(HIDDEN_KEYS.sales, item.id)
                              }
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {activeTab === "stock" && (
              <div className="table-responsive app-table-wrap">
                {hiddenStocks.length === 0 ? (
                  renderEmpty("stock")
                ) : (
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hiddenStocks.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Name">{item.name}</td>
                          <td data-label="SKU">{item.sku || "-"}</td>
                          <td data-label="Qty">{item.quantity}</td>
                          <td data-label="Price">{formatNumber(item.price)}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleRestore(HIDDEN_KEYS.stock, item.id)
                              }
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {activeTab === "loans" && (
              <div className="table-responsive app-table-wrap">
                {hiddenLoans.length === 0 ? (
                  renderEmpty("loan")
                ) : (
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hiddenLoans.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">{formatAppDate(item.loan_date)}</td>
                          <td data-label="Customer">
                            {item.customer?.name || "-"}
                          </td>
                          <td data-label="Type">{item.type}</td>
                          <td data-label="Amount">{formatNumber(item.amount)}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleRestore(HIDDEN_KEYS.loans, item.id)
                              }
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {activeTab === "salary" && (
              <div className="table-responsive app-table-wrap">
                {hiddenSalaries.length === 0 ? (
                  renderEmpty("salary")
                ) : (
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Employee</th>
                        <th>Amount</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hiddenSalaries.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDate(item.salary_date)}
                          </td>
                          <td data-label="Employee">{item.employee_name}</td>
                          <td data-label="Amount">{formatNumber(item.amount)}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleRestore(HIDDEN_KEYS.salary, item.id)
                              }
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {activeTab === "expenses" && (
              <div className="table-responsive app-table-wrap">
                {hiddenExpenses.length === 0 ? (
                  renderEmpty("expense")
                ) : (
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Title</th>
                        <th>Amount</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hiddenExpenses.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDate(item.expense_date)}
                          </td>
                          <td data-label="Title">{item.title}</td>
                          <td data-label="Amount">{formatNumber(item.amount)}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handleRestore(HIDDEN_KEYS.expenses, item.id)
                              }
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
